/**
 * Проверка целостности копии.
 *
 * У резервной копии есть особая беда: она не жалуется. Файл записался наполовину
 * — диск кончился, браузер закрыли, клиент облака перехватил запись, — и лежит
 * в папке с приличным именем и правдоподобным размером. Человек узнаёт об этом
 * ровно в тот момент, когда копия нужна, то есть когда исходных данных уже нет.
 *
 * Поэтому копия несёт с собой контрольную сумму и число записей, а приложение
 * сверяет их дважды: сразу после записи — перечитав то, что записалось, — и
 * перед восстановлением.
 *
 * ## Почему не SHA-256
 *
 * Криптографическая сумма защищает от подделки, а подделка здесь не при чём:
 * файл лежит у человека, и подписывать его нам нечем. Задача другая — заметить
 * порчу: обрыв записи, битый байт после переноса через облако, правку в
 * редакторе. С ней справляется простая быстрая свёртка, и она не требует ни
 * защищённого контекста, ни асинхронности.
 *
 * Старые копии контрольной суммы не несут. Отвергать их нельзя — это те самые
 * файлы, которые люди скачивали месяц назад; проверяется тогда только то, что
 * можно проверить: структура и число записей.
 */

import { countExtraRecords, type ExtraDatabases } from '@/shared/db/extra-databases';

/** Контрольная сумма содержимого — FNV-1a, 32 бита, в шестнадцатеричном виде. */
export function checksumOf(text: string): string {
  let hash = 0x811c9dc5;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    // Умножение на простое 16777619 через сдвиги: обычное переполнило бы
    // точность числа с плавающей точкой и дало бы разный результат на разных
    // строках одинаково.
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }

  return hash.toString(16).padStart(8, '0');
}

/** Что именно не сошлось. */
export type IntegrityProblem = 'checksum' | 'records';

export const INTEGRITY_MESSAGE: Record<IntegrityProblem, string> = {
  checksum: 'Файл копии повреждён: содержимое не совпадает с контрольной суммой.',
  records: 'Файл копии повреждён: часть записей потерялась при сохранении.',
};

interface Verifiable {
  tables: Record<string, unknown[]>;
  /** Базы съёмных разделов: в старых копиях поля нет вовсе. */
  databases?: ExtraDatabases;
  checksum?: string;
  records?: number;
}

/**
 * Сверяет копию с тем, что она сама о себе заявляет.
 *
 * `null` — сходится или проверять нечем: у копии из старой версии нет ни суммы,
 * ни счётчика, и объявлять её испорченной не за что.
 */
export function verifyBackup(backup: Verifiable): IntegrityProblem | null {
  if (typeof backup.records === 'number') {
    const actual =
      Object.values(backup.tables).reduce(
        (sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0),
        0,
      ) + countExtraRecords(backup.databases);
    if (actual !== backup.records) return 'records';
  }

  if (typeof backup.checksum === 'string' && backup.checksum !== checksumOf(payloadOf(backup))) {
    return 'checksum';
  }

  return null;
}

/**
 * Сумма считается по данным, а не по всей копии: иначе она включала бы себя.
 *
 * Копия без съёмных разделов сворачивается ровно так же, как сворачивалась
 * раньше: иначе все копии, снятые до их появления, разом объявились бы
 * испорченными.
 */
export function payloadOf(backup: Pick<Verifiable, 'tables' | 'databases'>): string {
  const databases = backup.databases;
  if (!databases || Object.keys(databases).length === 0) return JSON.stringify(backup.tables);

  return JSON.stringify({ tables: backup.tables, databases });
}
